<template>
  <div class="container">
    <article class="slds-card">
      <div class="slds-card__header slds-grid">
        <header class="slds-media slds-media_center slds-has-flexi-truncate">
          <div class="slds-media__figure">
            <span class="slds-icon_container slds-icon-standard-event">
              <svg class="slds-icon slds-icon-action-log-this-event" aria-hidden="true">
                <use xlink:href="../assets/symbols.svg#event" />
              </svg>
            </span>
          </div>
          <div class="slds-media__body">
            <h3>
              <span>{{ $t('sections.executionRules.exclusionRules.label') }}</span>
            </h3>
            <h2 class="slds-card__header-title">
              <span class="slds-card__header-link slds-truncate" :title="textRange">
                {{ textRange }}
              </span>
            </h2>

          </div>
          <div>
            <div class="slds-button-group" role="group">
              <button
                class="slds-button slds-button_icon slds-button_icon-border-filled"
                @click="decrement"
                :disabled="!enabled"
                :title="$t('general.previous')">
                <svg class="slds-button__icon" aria-hidden="true">
                  <use xlink:href="../assets/symbols.svg#chevronleft"></use>
                </svg>
              </button>
              <button
                class="slds-button slds-button_icon slds-button_icon-border-filled"
                @click="increment"
                :disabled="!enabled"
                :title="$t('general.next')">
                <svg class="slds-button__icon" aria-hidden="true">
                  <use xlink:href="../assets/symbols.svg#chevronright"></use>
                </svg>
              </button>

              <button class="slds-button slds-button_neutral" @click="goToToday" :disabled="isTodayInRange || !enabled">
                {{ $t('executionRules.exclusionRules.today') }}
              </button>
            </div>

            <MenuButton
              labels-prefix="executionRules.exclusionRules.viewMode"
              position="right"
              :disabled="!enabled"
              :options="viewModes"
              :value="viewMode"
              @select="handleSelect" />

              <div class="slds-button-group" role="group">
                <button class="slds-button slds-button_icon slds-button_icon-border-filled" :class="{'slds-is-selected': showSidebar}" aria-pressed="true" @click="toggleSidebar">
                  <svg class="slds-button__icon" aria-hidden="true">
                    <use xlink:href="../assets/symbols.svg#rows"></use>
                  </svg>
                </button>
              </div>
          </div>
        </header>
      </div>
    </article>
    <div class="row">
      <div class="column">
        <article class="slds-card calendar-wrapper" >
          <div class="full-height">
            <Calendar
              class="calendar container"
              show-days-number
              type="exclusionSlots"
              :days="daysInRange"
              :disabled="!enabled"
              :elements="filteredSlots"
              :last-seen="lastSeen"
              :view-mode="viewMode"
              @dayClick="toggleSlot"
              @yearChange="changeYear" />
            <div class="calendar-alert" v-if="!enabled">
              <div class="slds-text-heading_large slds-text-align_center">
                <span v-if="!calendar">{{$t('executionRules.exclusionRules.selectCalendar')}}</span>
                <span v-else>{{$t('executionRules.exclusionRules.selectMarketOrCountry')}}</span>
              </div>
            </div>
          </div>
        </article>
      </div>
      <article class="slds-card sidebar" v-if="showSidebar">
        <div class="slds-card__body slds-card__body_inner">
          <div class="sidebar">
            <Picklist :label="$t('executionRules.exclusionRules.calendar.label')" v-model="calendar" :options="calendars" labels-prefix="executionRules.exclusionRules.calendar" />
            <template v-if="calendar">
              <Picklist :label="$t('executionRules.exclusionRules.market')" v-model="market" :options="markets" labels-prefix="markets" v-if="calendar === 'CC'" />
            </template>
            <DatePicker v-model="lastSeen" @changeDate="goToDate" :disabled="!enabled" />
          </div>
        </div>
      </article>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import Calendar from '../components/exclusion-rules/Calendar'
import DatePicker from '../components/slds/DatePicker'
import Picklist from '../components/slds/Picklist'
import MenuButton from '../components/slds/MenuButton'
import { getErrorMessage } from '../components/utils/utils'
import CheckPermission from '../components/utils/CheckPermission'
export default {
  mixins: [CheckPermission],
  components: {
    Calendar,
    DatePicker,
    MenuButton,
    Picklist
  },
  computed: {
    enabled () {
      return this.calendar && (this.market || this.country)
    },
    filteredSlots () {
      if (this.enabled && this.exclusionRules[this.calendar]) {
        if (this.market) {
          return this.exclusionRules[this.calendar].filter(rule => {
            return rule.codMarket === this.market
          })
        } else if (this.country) {
          return this.exclusionRules[this.calendar].filter(rule => {
            return rule.codCountry === this.country
          })
        } else {
          return []
        }
      } else {
        return []
      }
    }
  },
  async created () {
    try {
      this.viewMode = 'monthly'
      this.range = this.calculateRange()
      this.textRange = this.calculateTextRange()
      this.isTodayInRange = this.calculateIsTodayInRange()
      this.daysInRange = this.calculateDaysInRange()

      const settings = await this.$store.dispatch('settings/getSettings')
      this.markets = settings.calendarMarkets.sort()
      this.countries = settings.countries.map(c => Object.keys(c)).flat()

      this.exclusionRules = await this.$store.dispatch('executionRules/getExclusionSlots')

      // Watch for locale change
      this.$store.watch(
        (state, getters) => getters['session/locale'],
        newValue => {
          this.range = this.calculateRange()
          this.textRange = this.calculateTextRange()
          this.isTodayInRange = this.calculateIsTodayInRange()
          this.daysInRange = this.calculateDaysInRange()
        },
        {
          deep: true
        }
      )
    } catch (error) {
      this.showToast('error', this.$t('general.error'), 'text', error)
    }
  },
  data () {
    return {
      countries: [],
      country: '',
      calendar: '',
      calendars: ['CC'],
      daysInRange: [],
      exclusionRules: [],
      markets: [],
      market: '',
      isTodayInRange: false,
      lastSeen: moment(new Date()),
      range: [],
      showSidebar: true,
      textRange: '',
      viewMode: '',
      viewModes: ['daily', 'weekly', 'monthly']
    }
  },
  methods: {
    calculateDaysInRange () {
      if (!Array.isArray(this.range)) {
        return [this.range]
      } else {
        const today = moment()
        if (this.viewMode === 'yearly') {
          const days = []
          _.range(-6, 0, 1)
            .concat(_.range(6))
            .forEach(year => days.push(today.year() + year))

          return days
        } else {
          const days = []
          let startDate = this.range[0]

          if (this.viewMode === 'monthly') {
            startDate = startDate.startOf('week')
          }

          const endDate = (this.range[1].add(1, 'd'))

          let date = startDate

          while ((date.isBefore(endDate)) || (days.length % 7 !== 0)) {
            days.push(date.clone())
            date = date.add(1, 'd')
          }

          return days
        }
      }
    },
    calculateIsTodayInRange () {
      if (Array.isArray(this.range)) {
        return moment().isBetween(this.range[0], this.range[1])
      } else {
        return moment().diff(this.lastSeen, 'days') === 0
      }
    },
    calculateRange () {
      this.lastSeen.locale(this.$store.getters['session/locale'])
      switch (this.viewMode) {
        case 'daily':
          return this.lastSeen
        case 'monthly':
          return [
            moment(new Date(this.lastSeen.year(), this.lastSeen.month(), 1)),
            moment(new Date(this.lastSeen.year(), this.lastSeen.month() + 1, 0))
          ]
        case 'weekly':
          return [
            moment(new Date(this.lastSeen.year(), this.lastSeen.month(), this.lastSeen.date() - this.lastSeen.weekday())),
            moment(new Date(this.lastSeen.year(), this.lastSeen.month(), this.lastSeen.date() + 6 - this.lastSeen.weekday()))
          ]
        case 'yearly':
          return [
            moment(new Date(this.lastSeen.year(), 0, 1)),
            moment(new Date(this.lastSeen.year(), 11, 31))
          ]
        default:
          return undefined
      }
    },
    calculateTextRange () {
      const localizedMonthName = this.$t(`months[${this.lastSeen.month()}]`)
      switch (this.viewMode) {
        case 'daily':
          return `${this.lastSeen.date()} ${localizedMonthName} ${this.lastSeen.year()}`
        case 'weekly':
          const from = this.range[0].format(this.$t('general.dateFormat'))
          const to = this.range[1].format(this.$t('general.dateFormat'))
          return `${from} - ${to}`
        case 'monthly':
          return `${localizedMonthName} ${this.lastSeen.year()}`
        case 'yearly':
          return this.lastSeen.year()
        default:
          return undefined
      }
    },
    changeYear (event) {
      this.lastSeen.year(event.year)
      this.viewMode = 'monthly'
      this.calculateAll()
    },
    decrement () {
      switch (this.viewMode) {
        case 'daily':
          this.lastSeen = moment(this.lastSeen.subtract(1, 'd'))
          break
        case 'weekly':
          this.lastSeen = moment(this.lastSeen.subtract(1, 'w'))
          break
        case 'monthly':
          this.lastSeen = moment(this.lastSeen.subtract(1, 'M'))
          break
        case 'yearly': {
          this.lastSeen = moment(this.lastSeen.subtract(1, 'y'))
          break
        }
      }
      this.calculateAll()
    },
    increment () {
      switch (this.viewMode) {
        case 'daily':
          this.lastSeen = moment(this.lastSeen.add(1, 'd'))
          break
        case 'weekly':
          this.lastSeen = moment(this.lastSeen.add(1, 'w'))
          break
        case 'monthly':
          this.lastSeen = moment(this.lastSeen.add(1, 'M'))
          break
        case 'yearly':
          this.lastSeen = moment(this.lastSeen.add(1, 'y'))
          break
      }

      this.calculateAll()
    },
    handleSelect (event) {
      this.viewMode = event.value
      this.calculateAll()
    },
    goToDate (event) {
      this.lastSeen = moment(event.day)
      this.viewMode = 'daily'
    },
    goToToday () {
      this.lastSeen = moment(new Date())
    },
    calculateAll () {
      this.range = this.calculateRange()
      this.textRange = this.calculateTextRange()
      this.isTodayInRange = this.calculateIsTodayInRange()
      this.daysInRange = this.calculateDaysInRange()
    },
    async toggleSlot (event) {
      if (this.checkPermission('MANAGE_EXECUTION_RULE')) {
        const { day, action } = event

        try {
          if (action === 'add') {
            const slot = {
              codCountry: this.country || null,
              codMarket: this.market || null,
              type: this.calendar,
              date: moment(day).format('YYYY-MM-DD')
            }
            await this.$store.dispatch('executionRules/saveExclusionSlot', slot)
            this.showToast('success', this.$t('general.success'), 'text', this.$t('message.create'))
            this.exclusionRules = await this.$store.dispatch('executionRules/getExclusionSlots')
          } else if (action === 'remove') {
            const slot = day.elements[0]
            await this.$store.dispatch('executionRules/deleteExclusionSlot', slot.id)
            this.showToast('success', this.$t('general.success'), 'text', this.$t('message.delete'))
            this.exclusionRules = await this.$store.dispatch('executionRules/getExclusionSlots')
          }
        } catch (error) {
          console.error(error)
          this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
        }
      }
    },
    toggleSidebar () {
      this.showSidebar = !this.showSidebar
    }
  },
  watch: {
    calendar () {
      this.country = ''
      this.market = ''
    },
    lastSeen () {
      this.calculateAll()
    }
  }
}
</script>
<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .calendar {
    flex-grow: 1;
  }

  .legend {
    margin-top: 10px;
  }

  .sidebar {
    margin-top: 10px;
  }

  .column {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .row {
    flex-grow: 1;
    display: flex;
    flex-direction: row;

    & > .column {
      &:not(:last-child) {
        margin-right: 10px;
      }

      & > article {
        margin-top: 10px;

        &:first-child {
          flex-grow: 1;
        }
      }
    }
  }
  .slds-button-group:not(:first-child) {
    margin-left: 10px !important;
  }

  .full-height {
    height: 100%;
  }

  .sidebar {
    display: flex;
    flex-direction: column;

    & > button {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .calendar-alert {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;

    & > div {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
</style>
